import React, { useEffect } from "react";

interface SeoProps {
  title: string;
  description: string;
  path?: string;
  imageUrl?: string;
  type?: string;
  robots?: string;
}

function upsertMetaTag(attr: "name" | "property", key: string, content: string) {
  if (!content) return;
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}='${key}']`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  if (!href) return;
  let link = document.head.querySelector<HTMLLinkElement>("link[rel='canonical']");
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function upsertAlternate(hreflang: string, href: string) {
  if (!hreflang || !href) return;
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel='alternate'][hreflang='${hreflang}']`);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", hreflang);
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function upsertJsonLd(id: string, json: object) {
  let script = document.head.querySelector<HTMLScriptElement>(`script#${id}`);
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(json);
}

const BASE_URL = "https://www.bollenoord.com";
const SITE_NAME = "BolleNoord 't Zand B.V.";

const COMPANY = {
  name: SITE_NAME,
  url: BASE_URL,
  telephone: "+31 224 590404",
  email: "info@bollenoord.com",
  address: {
    streetAddress: "Groteweg 9",
    postalCode: "1756 CK",
    addressLocality: "'t Zand",
    addressRegion: "Noord-Holland",
    addressCountry: "NL"
  },
  geo: { latitude: 52.835, longitude: 4.747 },
  sameAs: [] as string[]
};

const Seo: React.FC<SeoProps> = ({ title, description, path = "", imageUrl, type = "website", robots = "index,follow" }) => {
  useEffect(() => {
    document.title = title;

    upsertMetaTag("name", "description", description);
    upsertMetaTag("name", "robots", robots);

    upsertMetaTag("property", "og:title", title);
    upsertMetaTag("property", "og:description", description);
    upsertMetaTag("property", "og:type", type);
    upsertMetaTag("property", "og:site_name", SITE_NAME);
    upsertMetaTag("property", "og:locale", "nl_NL");

    const url = `${BASE_URL}${path}`.replace(/\/$/, "");
    upsertMetaTag("property", "og:url", url);

    if (imageUrl) {
      upsertMetaTag("property", "og:image", imageUrl);
    }

    upsertMetaTag("name", "twitter:card", imageUrl ? "summary_large_image" : "summary");
    upsertMetaTag("name", "twitter:title", title);
    upsertMetaTag("name", "twitter:description", description);
    if (imageUrl) {
      upsertMetaTag("name", "twitter:image", imageUrl);
    }

    upsertCanonical(url);

    // hreflang alternates for single-language site
    upsertAlternate("nl", url);
    upsertAlternate("x-default", url);

    const { geo, address } = COMPANY;
    upsertMetaTag("name", "geo.region", `${address.addressCountry}-${address.addressRegion}`);
    upsertMetaTag("name", "geo.placename", address.addressLocality);
    upsertMetaTag("name", "geo.position", `${geo.latitude};${geo.longitude}`);
    upsertMetaTag("name", "ICBM", `${geo.latitude}, ${geo.longitude}`);

    upsertJsonLd("org-jsonld", {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${BASE_URL}#organization`,
      name: COMPANY.name,
      url: COMPANY.url,
      telephone: COMPANY.telephone,
      email: COMPANY.email,
      image: imageUrl || undefined,
      address: {
        "@type": "PostalAddress",
        streetAddress: COMPANY.address.streetAddress,
        postalCode: COMPANY.address.postalCode,
        addressLocality: COMPANY.address.addressLocality,
        addressRegion: COMPANY.address.addressRegion,
        addressCountry: COMPANY.address.addressCountry
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: COMPANY.geo.latitude,
        longitude: COMPANY.geo.longitude
      },
      sameAs: COMPANY.sameAs,
    });

    upsertJsonLd("website-jsonld", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${BASE_URL}#website`,
      name: SITE_NAME,
      url: BASE_URL,
      inLanguage: "nl-NL"
    });

    return () => {};
  }, [title, description, path, imageUrl, type, robots]);

  return null;
};

export default Seo;
